import { useState } from 'react';
import type { FormErrors } from '@lib/forms';

type FormFieldElement = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

export function useFormFields<T extends Record<string, unknown>>(initialValues: T) {
  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<FormErrors<T>>({});

  const handleChange = (event: React.ChangeEvent<FormFieldElement>): void => {
    const target = event.target as FormFieldElement;
    const fieldName = target.name as keyof T;
    const isCheckbox = target instanceof HTMLInputElement && target.type === 'checkbox';

    setValues((prev) => {
      const currentValue = prev[fieldName];
      let nextValue: unknown;

      if (isCheckbox) {
        nextValue = target.checked;
      } else if (typeof currentValue === 'number') {
        nextValue = target.value === '' ? 0 : Number(target.value);
      } else {
        nextValue = target.value;
      }

      return {
        ...prev,
        [fieldName]: nextValue,
      };
    });

    setErrors((prev) => ({
      ...prev,
      [fieldName]: undefined,
    }));
  };

  const setFieldValue = (field: keyof T, nextValue: T[keyof T]): void => {
    setValues((prev) => ({
      ...prev,
      [field]: nextValue,
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: undefined,
    }));
  };

  const resetFields = (nextValues: T): void => {
    setValues(nextValues);
    setErrors({});
  };

  return {
    values,
    setValues,
    errors,
    setErrors,
    handleChange,
    setFieldValue,
    resetFields,
  };
}

